import * as React from 'react';
import { clsx } from 'clsx';

export type SectionIconVariant = 'warning' | 'danger' | 'info' | 'success';

export interface SectionIconProps {
  /**
   * The variant/style of the section icon
   * @default 'info'
   */
  variant?: SectionIconVariant;
  /**
   * Additional CSS class name
   */
  className?: string;
  /**
   * Additional inline styles
   */
  style?: React.CSSProperties;
}

// Warning triangle icon
const WarningIcon = ({ color }: { color: string }) => (
  <svg width="14" height="12" viewBox="0 0 14 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M7 0.5L13.5 11.5H0.5L7 0.5Z"
      stroke={color}
      strokeWidth="1.2"
      strokeLinejoin="round"
      fill="none"
    />
    <path
      d="M7 4.5V7"
      stroke={color}
      strokeWidth="1.2"
      strokeLinecap="round"
    />
    <circle cx="7" cy="9" r="0.75" fill={color} />
  </svg>
);

// Info circle icon
const InfoIcon = ({ color }: { color: string }) => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="7" cy="7" r="6" stroke={color} strokeWidth="1.2" fill="none" />
    <path d="M7 6V10" stroke={color} strokeWidth="1.2" strokeLinecap="round" />
    <circle cx="7" cy="4" r="0.75" fill={color} />
  </svg>
);

// Success checkmark icon
const SuccessIcon = ({ color }: { color: string }) => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="7" cy="7" r="6" stroke={color} strokeWidth="1.2" fill="none" />
    <path d="M4 7L6 9L10 5" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Variant configurations
const variantConfig: Record<SectionIconVariant, { backgroundColor: string; iconColor: string }> = {
  warning: {
    backgroundColor: '#fffaf5',
    iconColor: '#e4720d',
  },
  danger: {
    backgroundColor: '#fff5f5',
    iconColor: '#c93232',
  },
  info: {
    backgroundColor: '#f5fbff',
    iconColor: '#2c8bca',
  },
  success: {
    backgroundColor: '#f5fff8',
    iconColor: '#16a33d',
  },
};

// Arbor Design System section icon styles
const sectionIconStyles = {
  container: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px',
    borderRadius: '99px',
  },
  iconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '16px',
    height: '16px',
  },
};

/**
 * SectionIcon component - Arbor Design System
 *
 * A small badge-like icon used in section headings to indicate status or alerts.
 * Available in 4 variants: warning (orange), danger (red), info (blue), success (green).
 *
 * @example
 * ```tsx
 * <SectionIcon variant="warning" />
 * <SectionIcon variant="danger" />
 * <SectionIcon variant="info" />
 * <SectionIcon variant="success" />
 * ```
 */
export const SectionIcon = React.forwardRef<HTMLDivElement, SectionIconProps>(
  ({ variant = 'info', className, style, ...props }, ref) => {
    const config = variantConfig[variant];

    const renderIcon = () => {
      switch (variant) {
        case 'warning':
        case 'danger':
          return <WarningIcon color={config.iconColor} />;
        case 'success':
          return <SuccessIcon color={config.iconColor} />;
        case 'info':
        default:
          return <InfoIcon color={config.iconColor} />;
      }
    };

    return (
      <div
        ref={ref}
        className={clsx('arbor-section-icon', className)}
        style={{
          ...sectionIconStyles.container,
          backgroundColor: config.backgroundColor,
          ...style,
        }}
        {...props}
      >
        <div style={sectionIconStyles.iconWrapper}>
          {renderIcon()}
        </div>
      </div>
    );
  }
);

SectionIcon.displayName = 'SectionIcon';
